/**
 * Redaction (architecture doc 11.7): known secret values and common token patterns are
 * masked in transcripts, logs and events before they leave the machine agent. Pure and
 * browser-safe so the agent, the hub and the console's previews share it.
 */
import { isSecretEnvName } from './env-names.ts';

export interface TokenPattern {
  id: string;
  label: string;
  re: RegExp;
  /** Keep this capture group (a prefix such as 'Bearer ') and mask the rest of the match. */
  keep?: number;
}

/**
 * High-precision patterns: each matches a credential format by its shape, so ordinary text
 * and code are left alone. Order matters: specific formats before generic ones.
 */
export const TOKEN_PATTERNS: readonly TokenPattern[] = [
  { id: 'private-key', label: 'Private key', re: /-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z0-9 ]*PRIVATE KEY-----/g },
  { id: 'anthropic-key', label: 'Anthropic API key', re: /\bsk-ant-[A-Za-z0-9_-]{20,}/g },
  { id: 'openai-key', label: 'OpenAI API key', re: /\bsk-(?:proj-|svcacct-)?[A-Za-z0-9_-]{32,}/g },
  { id: 'github-token', label: 'GitHub token', re: /\b(?:gh[pousr]_[A-Za-z0-9]{36,}|github_pat_[A-Za-z0-9_]{22,})/g },
  { id: 'aws-access-key', label: 'AWS access key id', re: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g },
  { id: 'aws-secret', label: 'AWS secret key', re: /(aws_secret_access_key["']?\s*[=:]\s*["']?)[A-Za-z0-9/+=]{40}/gi, keep: 1 },
  { id: 'slack-token', label: 'Slack token', re: /\bxox[abposr]-[A-Za-z0-9-]{10,}/g },
  { id: 'slack-webhook', label: 'Slack webhook', re: /https:\/\/hooks\.slack\.com\/services\/[A-Za-z0-9/_-]{20,}/g },
  { id: 'google-key', label: 'Google API key', re: /\bAIza[0-9A-Za-z_-]{35}/g },
  { id: 'stripe-key', label: 'Stripe key', re: /\b(?:sk|rk|pk)_(?:live|test)_[A-Za-z0-9]{16,}/g },
  { id: 'npm-token', label: 'npm token', re: /\bnpm_[A-Za-z0-9]{36}\b/g },
  { id: 'jwt', label: 'JSON web token', re: /\beyJ[A-Za-z0-9_-]{8,}\.eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g },
  { id: 'bearer', label: 'Bearer token', re: /(\bBearer\s+)[A-Za-z0-9._~+/=-]{20,}/g, keep: 1 },
  { id: 'url-password', label: 'Password in a URL', re: /(\b[a-z][a-z0-9+.-]*:\/\/[^\s:/@]+:)[^\s@/]{3,}(?=@)/gi, keep: 1 },
  {
    id: 'assignment',
    label: 'Secret in an assignment',
    // NAME_TOKEN=..., "password": "...", api_key: ... with a value that looks generated (has a digit, 16+ chars).
    re: /((?:password|passwd|secret|token|api[_-]?key|access[_-]?key|client[_-]?secret)["']?\s*[=:]\s*["']?)(?=[A-Za-z0-9_\-+/=.]*\d)[A-Za-z0-9_\-+/=.]{16,}/gi,
    keep: 1,
  },
];

/** Values shorter than this are never redacted by value: masking them would hide ordinary text. */
export const MIN_REDACT_LENGTH = 6;

/** Values of this environment that look like secrets (by name), for redaction. */
export function secretEnvValues(env: Record<string, string | undefined>): { name: string; value: string }[] {
  const out: { name: string; value: string }[] = [];
  for (const [name, value] of Object.entries(env)) {
    if (!value || value.length < 8 || !isSecretEnvName(name)) continue;
    // Paths and booleans are configuration, not secrets.
    if (/^(true|false|0|1|yes|no)$/i.test(value) || (/[\\/]/.test(value) && !/[A-Za-z0-9+]{24,}/.test(value))) continue;
    out.push({ name, value });
  }
  return out;
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface Redactor {
  /** Mask secrets in one string. */
  text(s: string): string;
  /** A copy of any JSON-like value with every string redacted (the value itself when nothing matched). */
  deep<T>(value: T): T;
  /** How many replacements this redactor has made, per pattern id or 'value'. */
  readonly counts: Record<string, number>;
}

export interface RedactorOptions {
  /** Known secret values, with the name shown in the mask. */
  values?: Iterable<{ name: string; value: string }>;
  /** Apply TOKEN_PATTERNS (default true). */
  patterns?: boolean;
}

/** '[REDACTED:NAME]' for a known value, '[REDACTED:github-token]' for a pattern. */
export const mask = (what: string) => `[REDACTED:${what}]`;

export function createRedactor(opts: RedactorOptions = {}): Redactor {
  const named = new Map<string, string>();
  for (const v of opts.values ?? []) {
    if (!v || typeof v.value !== 'string' || v.value.length < MIN_REDACT_LENGTH) continue;
    if (!named.has(v.value)) named.set(v.value, v.name);
    // A value can also appear JSON-escaped (inside a tool input) or URL-encoded.
    const json = JSON.stringify(v.value).slice(1, -1);
    if (json !== v.value && !named.has(json)) named.set(json, v.name);
    const url = encodeURIComponent(v.value);
    if (url !== v.value && !named.has(url)) named.set(url, v.name);
  }
  const known = [...named.keys()].sort((a, b) => b.length - a.length);
  const valuesRe = known.length ? new RegExp(known.map(escapeRe).join('|'), 'g') : null;
  const patterns = opts.patterns === false ? [] : TOKEN_PATTERNS;
  const counts: Record<string, number> = {};
  const bump = (k: string) => {
    counts[k] = (counts[k] ?? 0) + 1;
  };

  function text(s: string): string {
    if (typeof s !== 'string' || s.length < MIN_REDACT_LENGTH) return s;
    let out = s;
    if (valuesRe) {
      out = out.replace(valuesRe, (m) => {
        bump('value');
        return mask(named.get(m) ?? 'secret');
      });
    }
    for (const p of patterns) {
      p.re.lastIndex = 0;
      if (!p.re.test(out)) continue;
      p.re.lastIndex = 0;
      out = out.replace(p.re, (...args: unknown[]) => {
        const m = args[0] as string;
        // Never re-mask a mask.
        if (m.startsWith('[REDACTED:')) return m;
        bump(p.id);
        const kept = p.keep ? ((args[p.keep] as string | undefined) ?? '') : '';
        return `${kept}${mask(p.id)}`;
      });
    }
    return out;
  }

  function deep<T>(value: T): T {
    if (typeof value === 'string') return text(value) as T;
    if (Array.isArray(value)) {
      let changed = false;
      const out = value.map((v) => {
        const r = deep(v);
        if (r !== v) changed = true;
        return r;
      });
      return (changed ? out : value) as T;
    }
    if (value && typeof value === 'object') {
      let changed = false;
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const r = deep(v);
        if (r !== v) changed = true;
        out[k] = r;
      }
      return (changed ? out : value) as T;
    }
    return value;
  }

  return { text, deep, counts };
}
